import { useMemo, useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';

var BASE_URL = "https://octane.cdn.ing/api/v1/images/transform?";
var DPR_MULTIPLIERS = [1, 2];
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
function useOptimizedImage(options) {
  const {
    src,
    eager = false,
    threshold = 0.1,
    rootMargin = "50px",
    width,
    height,
    optixFlowConfig
  } = options;
  const optixFlowApiKey = useMemo(() => {
    return optixFlowConfig?.apiKey;
  }, [optixFlowConfig?.apiKey]);
  const useOptixFlow = useMemo(() => {
    return optixFlowApiKey ? true : false;
  }, [optixFlowApiKey]);
  const [state, setState] = useState({
    isLoaded: false,
    isInView: false
  });
  const [measuredSize, setMeasuredSize] = useState({
    width: 0,
    height: 0
  });
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const size = useMemo(
    () => ({
      width: width ?? measuredSize.width,
      height: height ?? measuredSize.height
    }),
    [width, height, measuredSize.width, measuredSize.height]
  );
  useIsomorphicLayoutEffect(() => {
    if (!imgRef.current) return;
    if (width !== void 0 && height !== void 0) return;
    const calculateRenderedSize = () => {
      const img2 = imgRef.current;
      if (!img2) return;
      const renderedWidth = width ?? (Math.round(img2.clientWidth) || img2.naturalWidth || 0);
      const renderedHeight = height ?? (Math.round(img2.clientHeight) || img2.naturalHeight || 0);
      if (renderedWidth > 0 || renderedHeight > 0) {
        setMeasuredSize((prev) => {
          if (prev.width !== renderedWidth || prev.height !== renderedHeight) {
            return { width: renderedWidth, height: renderedHeight };
          }
          return prev;
        });
      }
    };
    if (imgRef.current.clientWidth > 0) {
      calculateRenderedSize();
    }
    const img = imgRef.current;
    img.addEventListener("load", calculateRenderedSize);
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        calculateRenderedSize();
      });
      resizeObserver.observe(img);
    }
    return () => {
      img.removeEventListener("load", calculateRenderedSize);
      resizeObserver?.disconnect();
    };
  }, [width, height]);
  const buildOptixFlowUrl = useCallback(
    (imgWidth, imgHeight, format) => {
      if (!useOptixFlow) return src;
      if (!imgWidth || !imgHeight) return src;
      const params = new URLSearchParams();
      params.set("url", src);
      params.set("fit", String(optixFlowConfig?.objectFit ?? "cover"));
      params.set("w", String(imgWidth));
      params.set("h", String(imgHeight));
      params.set("q", String(optixFlowConfig?.compressionLevel ?? 75));
      params.set("f", format);
      params.set("apiKey", optixFlowApiKey);
      return `${BASE_URL}${params.toString()}`;
    },
    [
      useOptixFlow,
      src,
      optixFlowConfig?.compressionLevel,
      optixFlowApiKey,
      optixFlowConfig?.objectFit
    ]
  );
  const generateSrcset = useCallback(
    (baseWidth, baseHeight, format) => {
      if (!useOptixFlow || baseWidth === 0 || baseHeight === 0) return "";
      return DPR_MULTIPLIERS.map((dpr) => {
        const scaledWidth = Math.round(baseWidth * dpr);
        const scaledHeight = Math.round(baseHeight * dpr);
        const url = buildOptixFlowUrl(scaledWidth, scaledHeight, format);
        return `${url} ${dpr}x`;
      }).join(", ");
    },
    [useOptixFlow, buildOptixFlowUrl]
  );
  const primarySrc = useMemo(() => {
    const hasDimensions = size.width > 0 && size.height > 0;
    if (!useOptixFlow || !hasDimensions) return src;
    const fallbackFormat = optixFlowConfig?.renderedFileType ?? "jpeg";
    return buildOptixFlowUrl(size.width, size.height, fallbackFormat);
  }, [
    useOptixFlow,
    src,
    size.width,
    size.height,
    optixFlowConfig?.renderedFileType,
    optixFlowConfig?.compressionLevel,
    optixFlowConfig?.objectFit,
    buildOptixFlowUrl
  ]);
  const srcset = useMemo(() => {
    return {
      avif: generateSrcset(size.width, size.height, "avif"),
      webp: generateSrcset(size.width, size.height, "webp"),
      jpeg: generateSrcset(size.width, size.height, "jpeg")
    };
  }, [size.width, size.height, generateSrcset]);
  const sizes = useMemo(() => {
    if (size.width === 0) return "";
    return `${size.width}px`;
  }, [size.width]);
  useEffect(() => {
    if (typeof window === "undefined" || !imgRef.current) {
      return;
    }
    if (eager) {
      setState({ isLoaded: false, isInView: true });
      return;
    }
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState((prev) => ({ ...prev, isInView: true }));
          observerRef.current?.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    observerRef.current.observe(imgRef.current);
    return () => {
      observerRef.current?.disconnect();
    };
  }, [eager, threshold, rootMargin]);
  useEffect(() => {
    if (!imgRef.current) return;
    const handleLoad = () => {
      setState((prev) => ({ ...prev, isLoaded: true }));
    };
    const img = imgRef.current;
    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener("load", handleLoad);
      return () => img.removeEventListener("load", handleLoad);
    }
  }, [state.isInView]);
  const ref = useCallback((node) => {
    imgRef.current = node;
  }, []);
  const emptySrcset = { avif: "", webp: "", jpeg: "" };
  return {
    ref,
    // Primary src uses exact rendered dimensions for Lighthouse "Properly size images" compliance
    src: state.isInView || eager ? primarySrc : src,
    // Srcset with format variants and DPR multipliers for <picture> element
    srcset: state.isInView || eager ? srcset : emptySrcset,
    // Sizes attribute for responsive image selection
    sizes: state.isInView || eager ? sizes : "",
    isLoaded: state.isLoaded,
    isInView: state.isInView,
    loading: eager ? "eager" : "lazy",
    size
  };
}

export { useOptimizedImage };
//# sourceMappingURL=chunk-ZTHTGLZO.js.map
//# sourceMappingURL=chunk-ZTHTGLZO.js.map